import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Filter,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import PageWrapper from "../components/common/PageWrapper";
import { ProductCard } from "../components/common/ProductCard";
import FiltersSidebar from "../components/common/FiltersSidebar";
import FilterDrawer from "./common/FilterDrawer";
import { applyStorefrontFilters, activeStorefrontFilters, productPrice } from "../utils/storefrontFilters";
import { apiRequest } from "../services/api";
import { getAvailableVariants } from "../utils/storefrontStock";

const getProductPrice = (product) => {
  const variants = product.variants || [];
  const availableVariants = getAvailableVariants(product);
  const variantsForPrice =
    availableVariants.length > 0 ? availableVariants : variants;

  const variant =
    variantsForPrice.find((item) => item.onlinePrice != null) ||
    variantsForPrice[0] ||
    {};

  return Number(
    product.price ??
      variant.onlinePrice ??
      variant.mrp ??
      0,
  );
};

const getProductSizes = (product) => {
  const variants = getAvailableVariants(product);

  return [
    ...new Set(
      variants
        .map((variant) => variant.size?.name || variant.size?.code)
        .filter(Boolean),
    ),
  ];
};

const normalizeForFilters = (product) => ({
  ...product,
  categoryDisplay: product.category,
  title: product.title || product.name || "",
  brand: product.brand?.name || product.brand || "",
  colors: [
    ...new Set(
      getAvailableVariants(product)
        .map((variant) => variant.color?.name)
        .filter(Boolean),
    ),
  ],
  category:
    product.category?.slug ||
    product.category?.name?.toLowerCase() ||
    product.category ||
    "",
  description:
    product.description ||
    product.shortDescription ||
    product.fullDescription ||
    "",
  price: productPrice(product),
  sizes: getProductSizes(product),
  rating: Number(product.rating || 0),
});

const initialFilters = {
  categoryIds: [], subCategoryIds: [], brands: [], sizes: [], colors: [], discount: 0,
  categoryId: '',
  subCategorySlug: '',
  minPrice: "",
  maxPrice: "",
  brand: "",
  size: "",
  color: "",
  rating: 0,
};

export default function SearchResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { query } = useParams();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    let active = true;
    apiRequest('/storefront/categories').then(data => { if (active) setCategories(data || []); }).catch(() => {});
    return () => { active = false; };
  }, []);
  const [loading, setLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");

  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState("featured");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const requestedSort = new URLSearchParams(location.search).get("sort");

    if (
      ["featured", "newest", "rating", "price_asc", "price_desc"].includes(
        requestedSort,
      )
    ) {
      setSort(requestedSort);
    }
  }, [location.search]);

  useEffect(() => {
    let mounted = true;

    const loadProducts = async () => {
      try {
        setLoading(true);
        setCatalogError("");

        const params = new URLSearchParams();
        if (filters.categoryId) params.set('category', filters.categoryId);
        if (filters.subCategorySlug) params.set('subCategory', filters.subCategorySlug);
        const response = await apiRequest(`/storefront/products${params.size ? `?${params}` : ''}`);

        if (mounted) {
          setProducts(response || []);
        }
      } catch (error) {
        console.error("Could not load storefront products:", error);

        if (mounted) {
          setCatalogError(
            error.message || "Could not load products. Please try again.",
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      mounted = false;
    };
  }, [filters.categoryId, filters.subCategorySlug]);

  const items = useMemo(() => {
    const normalizedProducts = products.map(normalizeForFilters);

    // /search/all means browse every product.
    const searchQuery =
      query && query.toLowerCase() !== "all" ? query.toLowerCase() : "";

    let result = normalizedProducts.filter((product) => {
      if (!searchQuery) return true;

      return (
        product.title.toLowerCase().includes(searchQuery) ||
        String(product.onlineName || '').toLowerCase().includes(searchQuery) ||
        String(product.productCode || '').toLowerCase().includes(searchQuery) ||
        product.brand.toLowerCase().includes(searchQuery) ||
        product.category.toLowerCase().includes(searchQuery) ||
        product.description.toLowerCase().includes(searchQuery)
      );
    });

    result = applyStorefrontFilters(result, filters);

    if (sort === "price_asc") {
      result = [...result].sort((a, b) => a.price - b.price);
    }

    if (sort === "price_desc") {
      result = [...result].sort((a, b) => b.price - a.price);
    }

    if (sort === "rating") {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }

    if (sort === "newest") {
      result = [...result].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      );
    }

    if (sort === "featured") {
      result = [...result].sort((a, b) => {
        if (a.isFeatured === b.isFeatured) {
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }

        return Number(b.isFeatured) - Number(a.isFeatured);
      });
    }

    return result;
  }, [products, query, filters, sort]);

  const isAllProducts = !query || query.toLowerCase() === "all";

  const pageTitle = isAllProducts
    ? "All products"
    : decodeURIComponent(query).replace(/-/g, " ");

  const activeFilterCount = activeStorefrontFilters(filters);

  const clearFilters = () => {
    setFilters(initialFilters);
    setShowFilters(false);
  };

  return (
    <PageWrapper>
      {/* Editorial page heading */}
      <section className="border-b border-[#eadde1] bg-[#faf7f5]">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary-800">
            {isAllProducts ? "The CHARMS collection" : "Search results"}
          </p>

          <div className="mt-3 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-4xl font-semibold capitalize tracking-[-0.045em] text-slate-950 sm:text-5xl lg:text-6xl">
                {pageTitle}
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
                {isAllProducts
                  ? "Explore premium fashion, fresh collections and your favourite brands."
                  : `Explore styles matching “${pageTitle}” from the CHARMS collection.`}
              </p>
            </div>

            <div className="border-l-2 border-primary-800 pl-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                Available online
              </p>

              <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">
                {loading ? "—" : items.length}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {catalogError ? (
          <div className="mb-7 border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-700">
            {catalogError}
          </div>
        ) : null}

        {/* Controls */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3 border-y border-slate-200 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowFilters((current) => !current)}
              className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-slate-900 transition hover:border-primary-800 hover:text-primary-800 lg:hidden"
            >
              <Filter size={16} />
              Filters
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>

            <div className="hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 lg:flex">
              <SlidersHorizontal size={15} />
              {loading ? "Loading collection" : `${items.length} products`}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label
              htmlFor="product-sort"
              className="hidden text-xs font-bold uppercase tracking-[0.12em] text-slate-500 sm:block"
            >
              Sort by
            </label>

            <select
              id="product-sort"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              className="border border-slate-300 bg-white px-3 py-2.5 text-xs font-bold uppercase tracking-[0.08em] text-slate-800 outline-none transition focus:border-primary-800"
            >
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="rating">Top Rated</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
        </div>

        {/* Mobile filters */}
        <FilterDrawer open={showFilters} onClose={()=>setShowFilters(false)} onClear={()=>setFilters(initialFilters)} count={items.length}>
          <FiltersSidebar showHeader={false} categories={categories} filters={filters} setFilters={setFilters} products={products} />
        </FilterDrawer>

        <div className="flex gap-10">
          {/* Desktop filters */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-32 max-h-[calc(100dvh-9rem)] overflow-y-auto overscroll-contain rounded-2xl bg-slate-50/70 p-3">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-950">Filters</h2>

                {activeFilterCount > 0 ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs font-bold uppercase tracking-[0.12em] text-primary-800 transition hover:text-primary-950"
                  >
                    Clear all
                  </button>
                ) : null}
              </div>

              <FiltersSidebar showHeader={false}
                categories={categories}
                filters={filters}
                setFilters={setFilters}
                products={products}
              />
            </div>
          </aside>

          {/* Product grid */}
          <div className="min-w-0 flex-1">
            {loading ? (
              <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div
                    key={index}
                    className="aspect-[3/4] animate-pulse bg-slate-100"
                  />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex min-h-[450px] flex-col items-center justify-center border border-dashed border-slate-300 bg-[#faf7f5] px-6 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-primary-800">
                  <Search size={23} />
                </div>

                <h3 className="mt-5 text-2xl font-semibold tracking-tight text-slate-950">
                  No products found
                </h3>

                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Try changing your filters or explore the complete CHARMS
                  collection.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    navigate("/search/all");
                  }}
                  className="mt-7 inline-flex items-center gap-2 bg-primary-800 px-5 py-3 text-sm font-bold text-white transition hover:bg-primary-950"
                >
                  Browse all products
                  <ArrowRight size={16} />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-2 xl:grid-cols-3">
                {items.map((item, index) => (
                  <ProductCard
                    key={item.id}
                    item={item}
                    index={index}
                    navigate={navigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
